import { X } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/cn";

interface ChipsFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: FieldPath<TValues>;
  label: string;
  /// Maximum chips. Past `max` the input disables.
  max?: number;
  /// Max chars per chip. Longer values trigger a soft draft error.
  maxLength?: number;
  /// Optional hint shown below the chip row.
  hint?: string;
  placeholder?: string;
  className?: string;
}

/// Enter-to-chip free-form tag editor wired to react-hook-form. Stores an
/// array of strings in the field value. Replaces the standalone
/// `ValueTagsInput` in kudos.
export function ChipsField<TValues extends FieldValues>({
  control,
  name,
  label,
  max = 10,
  maxLength = 50,
  hint,
  placeholder,
  className,
}: ChipsFieldProps<TValues>): ReactNode {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const tags = (field.value as ReadonlyArray<string> | undefined) ?? [];
        return (
          <ChipsBody
            label={label}
            hint={hint}
            placeholder={placeholder}
            max={max}
            maxLength={maxLength}
            className={className}
            value={tags}
            onChange={(next) => field.onChange([...next])}
          />
        );
      }}
    />
  );
}

interface BodyProps {
  label: string;
  hint?: string;
  placeholder?: string;
  max: number;
  maxLength: number;
  className?: string;
  value: ReadonlyArray<string>;
  onChange: (next: ReadonlyArray<string>) => void;
}

function ChipsBody({
  label,
  hint,
  placeholder,
  max,
  maxLength,
  className,
  value,
  onChange,
}: BodyProps): ReactNode {
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  function commit() {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setDraft("");
      return;
    }
    if (trimmed.length > maxLength) {
      setDraftError(`Tags must be ${maxLength} characters or fewer.`);
      return;
    }
    if (value.length >= max) {
      setDraftError(`Maximum ${max} tags.`);
      return;
    }
    if (value.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...value, trimmed]);
    setDraft("");
    setDraftError(null);
  }

  function remove(tag: string) {
    onChange(value.filter((t) => t !== tag));
    setDraftError(null);
  }

  const placeholderText =
    value.length >= max ? "Max tags reached" : (placeholder ?? "Press Enter to add");

  return (
    <FormItem className={className}>
      <FormLabel>{label}</FormLabel>
      <FormControl>
        <Input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (draftError) setDraftError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Backspace" && draft.length === 0 && value.length > 0) {
              remove(value[value.length - 1] ?? "");
            }
          }}
          placeholder={placeholderText}
          disabled={value.length >= max}
        />
      </FormControl>
      {hint ? <FormDescription>{hint}</FormDescription> : null}
      {draftError ? (
        <p role="alert" className="text-2xs text-destructive">
          {draftError}
        </p>
      ) : null}
      <FormMessage />
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {value.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => remove(tag)}
              className={cn(
                "group inline-flex items-center gap-1 rounded-full bg-pulse-soft px-2 py-0.5",
                "text-xs text-text-brand-strong transition-colors hover:bg-danger-bg hover:text-danger-fg",
              )}
              aria-label={`Remove ${tag}`}
            >
              <span>{tag}</span>
              <X aria-hidden="true" className="size-3 opacity-60 group-hover:opacity-100" />
            </button>
          ))}
        </div>
      ) : null}
    </FormItem>
  );
}
